import * as RadixDialog from '@radix-ui/react-dialog';
import { useQueries } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpen,
  Boxes,
  CornerDownLeft,
  GitPullRequestArrow,
  Hash,
  Loader2,
  PackageOpen,
  Plus,
  Rocket,
  Search,
  ShieldAlert,
  Ticket,
  User,
  type LucideIcon,
} from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import type { TicketSummary } from '@aurora/contracts';
import { get } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useSession } from '../../lib/session';
import { navFor, visibleCreateActions, visibleNav } from '../navigation/nav-items';

/**
 * Search everything, from anywhere.
 *
 * The box in the top bar used to be an input wired to nothing: it accepted
 * typing and did nothing with it. This is what it now opens. Every source is a
 * list endpoint that already exists and already enforces the caller's scope in
 * SQL, so a restricted agent searching here sees exactly what the ticket list
 * would show them — the palette adds no access, it only saves the trip.
 *
 * Sources are only queried when the person holds the view permission for
 * them. That is not the security boundary (the server is) but it avoids a row
 * of 403s for a requester whose search has no business touching the CMDB.
 */

interface PaletteResult {
  id: string;
  group: string;
  title: string;
  subtitle?: string | undefined;
  to: string;
  icon: LucideIcon;
}

interface Source {
  key: string;
  label: string;
  permission: string;
  fetch: (q: string) => Promise<PaletteResult[]>;
}

const PER_SOURCE = 5;

interface ListEnvelope<T> {
  data: T[];
}

/**
 * The prefixes records carry on their list screens, so "CHG-12" typed here
 * means what it means there. The palette used to call changes "CHN" while the
 * Changes list said "CHG", and did not recognise either when typed.
 */
const RECORD_PREFIXES: Array<{
  prefix: string;
  path: string;
  noun: string;
  permission: string;
  icon: LucideIcon;
}> = [
  { prefix: 'PRB', path: '/problems', noun: 'problem', permission: 'problem.view', icon: ShieldAlert },
  { prefix: 'CHG', path: '/changes', noun: 'change', permission: 'change.view', icon: GitPullRequestArrow },
  { prefix: 'REL', path: '/releases', noun: 'release', permission: 'release.view', icon: Rocket },
];

const SOURCES: Source[] = [
  {
    key: 'tickets',
    label: 'Tickets',
    permission: 'ticket.view',
    fetch: async (q) => {
      const response = await get<ListEnvelope<TicketSummary>>('/tickets', { q, per_page: PER_SOURCE });
      return response.data.map((ticket) => ({
        id: `ticket-${ticket.id}`,
        group: 'Tickets',
        title: ticket.subject,
        subtitle: [`#${ticket.number}`, ticket.status.label, ticket.requester?.name].filter(Boolean).join(' · '),
        to: `/tickets/${ticket.number}`,
        icon: Ticket,
      }));
    },
  },
  {
    key: 'catalog',
    label: 'Service catalog',
    permission: 'catalog.view',
    fetch: async (q) => {
      const response = await get<ListEnvelope<{ id: number; name: string; short_description: string | null }>>(
        '/catalog/items',
        { q, per_page: PER_SOURCE },
      );
      return response.data.slice(0, PER_SOURCE).map((item) => ({
        id: `catalog-${item.id}`,
        group: 'Service catalog',
        title: item.name,
        subtitle: item.short_description ?? undefined,
        to: `/catalog/${item.id}`,
        icon: PackageOpen,
      }));
    },
  },
  {
    key: 'knowledge',
    label: 'Knowledge base',
    permission: 'kb.view',
    fetch: async (q) => {
      const response = await get<ListEnvelope<{ id: number; title: string; summary: string | null }>>(
        '/knowledge/articles',
        { q, per_page: PER_SOURCE },
      );
      return response.data.map((article) => ({
        id: `kb-${article.id}`,
        group: 'Knowledge base',
        title: article.title,
        subtitle: article.summary ?? undefined,
        to: `/knowledge/${article.id}`,
        icon: BookOpen,
      }));
    },
  },
  {
    key: 'problems',
    label: 'Problems',
    permission: 'problem.view',
    fetch: async (q) => numbered('/problems', 'Problems', 'PRB', ShieldAlert, q),
  },
  {
    key: 'changes',
    label: 'Changes',
    permission: 'change.view',
    fetch: async (q) => numbered('/changes', 'Changes', 'CHG', GitPullRequestArrow, q),
  },
  {
    key: 'releases',
    label: 'Releases',
    permission: 'release.view',
    fetch: async (q) => numbered('/releases', 'Releases', 'REL', Rocket, q),
  },
  {
    key: 'assets',
    label: 'Assets',
    permission: 'asset.view',
    fetch: async (q) => {
      const response = await get<
        ListEnvelope<{ number: number; name: string; ci_type: { name: string } | null; hostname: string | null }>
      >('/assets', { q, per_page: PER_SOURCE });
      return response.data.map((asset) => ({
        id: `asset-${asset.number}`,
        group: 'Assets',
        title: asset.name,
        subtitle: [asset.ci_type?.name, asset.hostname].filter(Boolean).join(' · ') || undefined,
        to: `/assets/${asset.number}`,
        icon: Boxes,
      }));
    },
  },
  {
    key: 'people',
    label: 'People',
    permission: 'user.view',
    fetch: async (q) => {
      const response = await get<ListEnvelope<{ id: number; full_name: string; email: string; job_title: string | null }>>(
        '/users',
        { q, per_page: PER_SOURCE },
      );
      return response.data.map((person) => ({
        id: `person-${person.id}`,
        group: 'People',
        title: person.full_name,
        subtitle: [person.email, person.job_title].filter(Boolean).join(' · '),
        // Searched by address as well as named, so the person is on the first
        // page of the list even before the People screen reads `person`.
        to: `/admin/people?q=${encodeURIComponent(person.email)}&person=${person.id}`,
        icon: User,
      }));
    },
  },
];

async function numbered(
  path: string,
  group: string,
  prefix: string,
  icon: LucideIcon,
  q: string,
): Promise<PaletteResult[]> {
  const response = await get<ListEnvelope<{ number: number; title: string; status?: { name: string } | string | null }>>(
    path,
    { q, per_page: PER_SOURCE },
  );
  return response.data.map((row) => ({
    id: `${prefix}-${row.number}`,
    group,
    title: row.title,
    subtitle: `${prefix}-${row.number}`,
    to: `${path}/${row.number}`,
    icon,
  }));
}

/** Waits for typing to pause, so each keystroke is not eight requests. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { session, can } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, 180);
  const isAgent = session?.is_agent ?? false;

  // Each opening starts clean; yesterday's half-typed search is not today's.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  /**
   * A requester searching is usually looking for help, so the catalogue and
   * the knowledge base come before their own tickets. An agent is usually
   * looking for a ticket.
   */
  const sources = useMemo(() => {
    const permitted = SOURCES.filter((source) => can(source.permission));
    if (isAgent) return permitted;
    const order = ['catalog', 'knowledge', 'tickets'];
    return [...permitted].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [can, isAgent]);

  const searching = debounced.length >= 2;
  const results = useQueries({
    queries: sources.map((source) => ({
      queryKey: ['palette', source.key, debounced],
      queryFn: () => source.fetch(debounced),
      enabled: open && searching,
      staleTime: 30_000,
    })),
  });

  const destinations = useMemo<PaletteResult[]>(() => {
    const lower = trimmed.toLowerCase();
    const matches = (text: string, keywords: string[] = []) =>
      !lower || text.toLowerCase().includes(lower) || keywords.some((word) => word.includes(lower));

    const pages = visibleNav(navFor(isAgent), can)
      .flatMap((group) => group.items)
      .filter((item) => matches(item.label, item.keywords))
      .map<PaletteResult>((item) => ({
        id: `nav-${item.to}`,
        group: 'Go to',
        title: item.label,
        to: item.to,
        icon: ArrowRight,
      }));

    const creates = visibleCreateActions(isAgent, can)
      .filter((action) => matches(`new ${action.label}`, ['create', 'add', 'raise']))
      .map<PaletteResult>((action) => ({
        id: `create-${action.to}`,
        group: 'Create',
        title: isAgent || action.to !== '/tickets/new' ? `New ${action.label.toLowerCase()}` : 'Report an issue',
        to: action.to,
        icon: Plus,
      }));

    // Typing a ticket number is the single most common search on a desk, and
    // a problem, change or release number is the same thing for its list.
    const jump: PaletteResult[] = [];
    const ticketNumber = /^#?(\d{1,9})$/.exec(trimmed)?.[1];
    if (ticketNumber && can('ticket.view')) {
      jump.push({ id: `jump-${ticketNumber}`, group: 'Open', title: `Open ticket #${ticketNumber}`, to: `/tickets/${ticketNumber}`, icon: Hash });
    }
    const prefixed = /^([a-z]{3})-?(\d{1,9})$/i.exec(trimmed);
    const record = prefixed && RECORD_PREFIXES.find((candidate) => candidate.prefix === prefixed[1]!.toUpperCase());
    if (prefixed && record && can(record.permission)) {
      const number = prefixed[2]!;
      jump.push({
        id: `jump-${record.prefix}-${number}`,
        group: 'Open',
        title: `Open ${record.noun} ${record.prefix}-${number}`,
        to: `${record.path}/${number}`,
        icon: record.icon,
      });
    }

    // With nothing typed, show the map; once typing, keep destinations brief so
    // search results are not pushed below the fold.
    return [...jump, ...(lower ? [...creates, ...pages].slice(0, 4) : [...creates, ...pages])];
  }, [trimmed, isAgent, can]);

  /**
   * The search that always works.
   *
   * Before any result arrives — and when none matches — Enter used to do
   * nothing at all. Searching the ticket list for the words is always a
   * sensible answer, so it is always offered: first for an agent, whose search
   * is usually for a ticket, and last for a requester, who is usually looking
   * for help rather than for their own requests.
   */
  const searchAll: PaletteResult[] =
    trimmed && can('ticket.view')
      ? [
          {
            id: 'search-tickets',
            group: 'Search',
            title: isAgent ? `Search all tickets for “${trimmed}”` : `Search my requests for “${trimmed}”`,
            to: `/tickets?q=${encodeURIComponent(trimmed)}`,
            icon: Search,
          },
        ]
      : [];

  const found = searching ? results.flatMap((result) => result.data ?? []) : [];
  const jumps = destinations.filter((item) => item.group === 'Open');
  const rest = destinations.filter((item) => !jumps.includes(item));
  const items = isAgent
    ? [...jumps, ...searchAll, ...rest, ...found]
    : [...jumps, ...rest, ...found, ...searchAll];
  const loading = searching && results.some((result) => result.isFetching);
  const failed = searching
    ? sources.filter((_, index) => results[index]?.isError).map((source) => source.label)
    : [];

  // Keep the highlighted row valid as results arrive and leave.
  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(items.length - 1, 0)));
  }, [items.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const choose = (item: PaletteResult | undefined) => {
    if (!item) return;
    onOpenChange(false);
    navigate(item.to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (items.length === 0 ? 0 : (current + 1) % items.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (items.length === 0 ? 0 : (current - 1 + items.length) % items.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(items[active]);
    }
  };

  // Rows grouped under headings, in the order they arrived.
  const grouped: Array<{ group: string; rows: Array<{ item: PaletteResult; index: number }> }> = [];
  items.forEach((item, index) => {
    const last = grouped[grouped.length - 1];
    if (last && last.group === item.group) last.rows.push({ item, index });
    else grouped.push({ group: item.group, rows: [{ item, index }] });
  });

  const activeId = items[active] ? `palette-option-${active}` : undefined;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-[1px]" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            'animate-pop fixed top-[10vh] left-1/2 z-50 flex max-h-[72vh] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 flex-col',
            'overflow-hidden rounded-panel border border-line bg-raised shadow-float',
          )}
        >
          <RadixDialog.Title className="sr-only-focusable">Search</RadixDialog.Title>
          <div className="flex items-center gap-2.5 border-b border-line px-3.5">
            {loading ? (
              <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-ink-subtle" />
            ) : (
              <Search aria-hidden className="size-4 shrink-0 text-ink-subtle" />
            )}
            <input
              autoFocus
              dir="auto"
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-results"
              aria-activedescendant={activeId}
              aria-autocomplete="list"
              aria-label="Search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={
                isAgent ? 'Search tickets, people, assets, articles… or type a ticket number' : 'Search for help or a service…'
              }
              className="h-12 min-w-0 flex-1 bg-transparent text-reading text-ink outline-none placeholder:text-ink-subtle"
            />
            <kbd className="hidden shrink-0 rounded border border-line bg-sunken px-1.5 py-0.5 font-sans text-micro text-ink-subtle sm:inline">
              Esc
            </kbd>
          </div>

          <div ref={listRef} id="palette-results" role="listbox" aria-label="Results" className="scrollbar-slim min-h-0 flex-1 overflow-y-auto p-1.5">
            {grouped.map(({ group, rows }) => (
              <div key={group} role="group" aria-label={group} className="pb-1">
                <p className="px-2.5 pt-2 pb-1 text-mini font-semibold text-ink-subtle">{group}</p>
                {rows.map(({ item, index }) => (
                  <div
                    key={item.id}
                    id={`palette-option-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={index === active}
                    onMouseMove={() => setActive(index)}
                    onClick={() => choose(item)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 rounded-control px-2.5 py-2',
                      index === active ? 'bg-accent-soft text-accent-ink' : 'text-ink',
                    )}
                  >
                    <item.icon aria-hidden className={cn('size-4 shrink-0', index === active ? 'text-accent-ink' : 'text-ink-subtle')} />
                    <div className="min-w-0 flex-1">
                      <p dir="auto" className="truncate text-body font-medium">
                        {item.title}
                      </p>
                      {item.subtitle && (
                        <p dir="auto" className="truncate text-mini text-ink-muted">
                          {item.subtitle}
                        </p>
                      )}
                    </div>
                    {index === active && <CornerDownLeft aria-hidden className="size-3.5 shrink-0 opacity-70" />}
                  </div>
                ))}
              </div>
            ))}

            {searching && !loading && found.length === 0 && (
              <p className="px-3 py-6 text-center text-body text-ink-muted">
                Nothing found for “<span dir="auto">{debounced}</span>”.
              </p>
            )}
            {trimmed.length === 1 && (
              <p className="px-3 py-2 text-mini text-ink-subtle">Keep typing to search records.</p>
            )}
            {failed.length > 0 && (
              <p role="status" className="px-3 py-2 text-mini text-critical-ink">
                Couldn’t search {failed.join(', ')} just now.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 border-t border-line bg-sunken px-3.5 py-2 text-mini text-ink-muted">
            <span>
              <kbd className="font-sans">↑</kbd> <kbd className="font-sans">↓</kbd> to move
            </span>
            <span>
              <kbd className="font-sans">Enter</kbd> to open
            </span>
            <span className="ms-auto">
              <kbd className="font-sans">{isMac() ? '⌘' : 'Ctrl'} K</kbd> anywhere
            </span>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Opens the palette from somewhere other than the top bar — the requester
 * home page's search box, for one. A no-op outside the shell.
 */
export const PaletteContext = createContext<() => void>(() => {});
export const useOpenPalette = () => useContext(PaletteContext);

export function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/** True while typing somewhere, so "/" types a slash rather than opening search. */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Ctrl+K (⌘K on a Mac) from anywhere, and "/" when not typing — the two
 * conventions people already have in their fingers from other tools.
 */
export function usePaletteShortcut(setOpen: (update: (open: boolean) => boolean) => void) {
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((open) => !open);
      } else if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isEditable(event.target)) {
        event.preventDefault();
        setOpen(() => true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);
}
